import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
// EditSpaceServiceModal.tsx — Co Marca — Editar Espacio de Servicio
import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit, Store, Package, CheckCircle2 } from 'lucide-react';
import { updateEspacioServicio, EspacioServicio } from '../../services/espacioServiciosService';
import { fetchEspacios, fetchServicios, CatalogoItem } from '../../services/catalogosService';

interface EditSpaceServiceModalProps {
    isOpen: boolean; onClose: () => void; onSuccess: () => void; servicio: EspacioServicio|null;
}

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accentDark:     '#ea8406',
};

const css = `

.essm-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes essmFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes essmSlideUp{from{opacity:0;transform:translateY(24px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.essm-overlay{animation:essmFadeIn .22s ease both;}
.essm-dialog{animation:essmSlideUp .32s cubic-bezier(.16,1,.3,1) both;}
.essm-input{width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid var(--ad-line, #e5e7eb);background:#fafafa;font-size:.88rem;color:var(--ad-ink, #374151);outline:none;transition:all .2s;}
.essm-input:focus{border-color:${colors.secondary};box-shadow:0 0 0 3px color-mix(in srgb, ${colors.secondary} 7.06%, transparent);background:var(--ad-surface, #fff);}
.essm-select{appearance:none;}
.essm-btn-primary{display:inline-flex;align-items:center;gap:7px;background:linear-gradient(135deg,${colors.secondary},#004d7a);color:#fff;border:none;border-radius:11px;padding:10px 22px;font-size:.88rem;font-weight:800;cursor:pointer;box-shadow:0 4px 14px rgba(97, 37, 123,.28);transition:all .22s cubic-bezier(.16,1,.3,1);}
.essm-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(97, 37, 123,.34);}
.essm-btn-primary:disabled{background:var(--ad-line, #e2e8f0);color:var(--ad-muted, #94a3b8);box-shadow:none;cursor:not-allowed;}
.essm-btn-ghost{display:inline-flex;align-items:center;gap:7px;background:var(--ad-soft, #f8fafc);border:1.5px solid var(--ad-line, #e2e8f0);color:var(--ad-ink, #374151);border-radius:11px;padding:10px 18px;font-size:.88rem;font-weight:700;cursor:pointer;transition:all .2s;}
.essm-btn-ghost:hover{background:var(--ad-line, #f1f5f9);}
.custom-scroll::-webkit-scrollbar{width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.3);border-radius:20px;}
`;

const EditSpaceServiceModal: React.FC<EditSpaceServiceModalProps> = ({ isOpen, onClose, onSuccess, servicio }) => {
    const [formData, setFormData] = useState({ espacio:'', servicio:'', estado:true });
    const [espaciosList, setEspaciosList] = useState<CatalogoItem[]>([]);
    const [serviciosList, setServiciosList] = useState<CatalogoItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError]         = useState<string|null>(null);

    useEffect(() => {
        if (servicio) setFormData({ espacio:servicio.espacio.toString(), servicio:servicio.servicio.toString(), estado:servicio.estado });
    }, [servicio]);

    useEffect(() => {
        if (!isOpen) return;
        setError(null);
        fetchEspacios().then(d=>setEspaciosList(d.filter((i:any)=>i.estado!==false))).catch(()=>setEspaciosList([]));
        fetchServicios().then(d=>setServiciosList(d.filter((i:any)=>i.estado!==false))).catch(()=>setServiciosList([]));
    }, [isOpen]);

    const handleSelect = (e: ChangeEvent<HTMLSelectElement>) => setFormData(p=>({...p,[e.target.name]:e.target.value}));

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!servicio) return;
        setError(null); setIsLoading(true);
        try {
            await updateEspacioServicio(servicio.id, { espacio:Number(formData.espacio), servicio:Number(formData.servicio), estado:formData.estado });
            onSuccess(); onClose();
        } catch { setError('Error al actualizar el espacio de servicio.'); }
        finally { setIsLoading(false); }
    };

    if (!isOpen || !servicio) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="essm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="essm-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>
                <LumaAccessiblePanel data-luma-dialog="true" className="essm-dialog custom-scroll card custom-card vx-card" style={{ position:'relative', background:'var(--ad-surface, #fff)', borderRadius:'24px', width:'100%', maxWidth:'500px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid var(--ad-line, #f1f5f9)' }}>

                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.secondary},${colors.secondaryLight},${colors.accentDark},${colors.primary})`, borderRadius:'24px 24px 0 0' }}/>

                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.secondary},#004d7a)`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px color-mix(in srgb, ${colors.secondary} 18.82%, transparent)` }}>
                                    <Edit size={17} color="#fff"/>
                                </div>
                                <div>
                                    <LumaAccessibleTitle style={{ fontSize:'1.1rem', fontWeight:'650', color:'var(--ad-ink, #0f172a)', letterSpacing:'-.02em' }}>Editar Asociación</LumaAccessibleTitle>
                                    <p style={{ fontSize:'.72rem', color:'var(--ad-muted, #94a3b8)', fontWeight:'500' }}>Modifica la vinculación espacio-servicio</p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px' }}>
                                {[colors.secondary,colors.secondaryLight,colors.accentDark,colors.primary].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid var(--ad-line, #f1f5f9)', background:'var(--ad-soft, #f8fafc)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='var(--ad-line, #f1f5f9)'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='var(--ad-soft, #f8fafc)'}>
                            <X size={15} color="var(--ad-muted, #64748b)"/>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'16px' }}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`color-mix(in srgb, ${colors.primary} 3.14%, transparent)`, border:`1.5px solid color-mix(in srgb, ${colors.primary} 13.33%, transparent)`, borderRadius:'10px', padding:'10px 14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:'var(--ad-link, #2854c5)', fontWeight:'600' }}>{error}</span>
                            </div>
                        )}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Package size={11} color={colors.primary}/> Servicio *
                                </label>
                                <select required name="servicio" value={formData.servicio} onChange={handleSelect} className="essm-input essm-select">
                                    <option value="">Selecciona...</option>
                                    {serviciosList.map(i=><option key={i.id} value={i.id}>{i.nombre}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Store size={11} color={colors.secondary}/> Espacio *
                                </label>
                                <select required name="espacio" value={formData.espacio} onChange={handleSelect} className="essm-input essm-select">
                                    <option value="">Selecciona...</option>
                                    {espaciosList.map(i=><option key={i.id} value={i.id}>{i.nombre}</option>)}
                                </select>
                            </div>
                        </div>
                        <div style={{ background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #f1f5f9)', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }} className="card custom-card vx-card">
                            <div>
                                <p style={{ fontSize:'.85rem', fontWeight:'700', color:'var(--ad-ink, #0f172a)', marginBottom:'2px' }}>Estado</p>
                                <p style={{ fontSize:'.7rem', color:formData.estado?colors.secondaryLight:'var(--ad-muted, #94a3b8)', fontWeight:'600' }}>
                                    {formData.estado?'● Activo':'○ Inactivo'}
                                </p>
                            </div>
                            <div onClick={()=>setFormData(p=>({...p,estado:!p.estado}))} style={{ width:'44px', height:'24px', borderRadius:'100px', background:formData.estado?colors.secondary:'#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                <div style={{ position:'absolute', top:'3px', left:formData.estado?'23px':'3px', width:'18px', height:'18px', borderRadius:'50%', background:'var(--ad-surface, #fff)', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                            </div>
                        </div>
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} disabled={isLoading} className="essm-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={isLoading} className="essm-btn-primary btn btn-primary">
                                {isLoading ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Guardando...</> : <><CheckCircle2 size={14}/> Guardar Cambios</>}
                            </button>
                        </div>
                    </form>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>,
        document.body
    );
};

export default EditSpaceServiceModal;
